import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { routes } from '../../constants.js/misc';
import {
    buildCartDiscountTierTogglePayload,
    formatCartDiscountTierSummary,
    mapCartDiscountTierToListRow,
} from '../../constants/cartDiscountTierConfig';
import {
    CART_SUBTOTAL_FEATURE_NAME,
    CART_SUBTOTAL_PAGE_SUBTITLE,
    CART_SUBTOTAL_CREATE_DISABLED_HINT,
    CART_SUBTOTAL_TYPE_LABEL,
    PROMOTION_STATUS_LABELS,
    PROMOTIONS_TAB_CART_SUBTOTAL,
    STOREFRONT_PROMOTIONS_ENABLED,
    formatPromotionDiscount,
    getStrategyLabel,
    isPromotionsFeatureEnabled,
    parsePromotionsTabFromSearch,
} from '../../constants/promotionConfig';
import {
    TIER_SCOPE_LABELS,
    buildTierUpdatePayload,
    formatTierRuleSummary,
    mapPricingTierToListRow,
    parsePricingTiersListResponse,
} from '../../constants/pricingTierConfig';
import {
    deleteCartDiscountTierService,
    getCartDiscountTierService,
    parseCartDiscountTierFromResponse,
    upsertCartDiscountTierService,
} from '../../services/cartDiscountTier';
import {
    deletePricingTierService,
    getPricingTiersService,
    updatePricingTierService,
} from '../../services/pricingTiers';
import {
    deletePromotionService,
    getPromotionsService,
    updatePromotionService,
} from '../../services/promotions';
import PromotionsPageMobile from './PromotionsPageMobile';
import { usePromotionsActionButtonStyles, usePromotionsTableStyles } from './PromotionsPage.styles';
import TierDiscountSummary from './components/TierDiscountSummary';
import CartDiscountTierSummary from './components/CartDiscountTierSummary';
import PromotionsBetaNotice from './components/PromotionsBetaNotice';
import PromotionsPageTabs from './components/PromotionsPageTabs';
import LimitedFreemiumPlan from '../../components/LimitedFreemiumPlan';

const parsePromotionsListResponse = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object' && Array.isArray(raw.promotions)) return raw.promotions;
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data;
    return [];
};

const PromotionsPage = ({ storeId, storeData }) => {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const actionButtonClasses = usePromotionsActionButtonStyles();
    const tableClasses = usePromotionsTableStyles();

    const [rules, setRules] = useState([]);
    const [cartRule, setCartRule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(() =>
        parsePromotionsTabFromSearch(location.search)
    );

    const storeCurrency = storeData?.currency || storeData?.store_plan?.currency;
    const promotionsFeatureEnabled = isPromotionsFeatureEnabled(storeData);
    const isCartSubtotalTab = activeTab === PROMOTIONS_TAB_CART_SUBTOTAL;
    const displayRules = isCartSubtotalTab
        ? cartRule
            ? [mapCartDiscountTierToListRow(cartRule)]
            : []
        : rules;
    const isPrimaryCreateDisabled = isCartSubtotalTab && Boolean(cartRule);

    useEffect(() => {
        setActiveTab(parsePromotionsTabFromSearch(location.search));
    }, [location.search]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        const params = new URLSearchParams(location.search);
        if (tab === PROMOTIONS_TAB_CART_SUBTOTAL) {
            params.set('tab', PROMOTIONS_TAB_CART_SUBTOTAL);
        } else {
            params.delete('tab');
        }
        const search = params.toString();
        history.replace({
            pathname: routes.promotionsPage,
            search: search ? `?${search}` : '',
        });
    };

    const loadRules = useCallback(() => {
        if (!storeId || !promotionsFeatureEnabled) return;
        setLoading(true);

        const quantityRequest = getPricingTiersService(storeId).catch(() => ({ data: [] }));
        const cartRequest = getCartDiscountTierService(storeId).catch(() => ({ data: null }));
        const requests = STOREFRONT_PROMOTIONS_ENABLED
            ? [getPromotionsService(storeId).catch(() => ({ data: [] })), quantityRequest, cartRequest]
            : [quantityRequest, cartRequest];

        Promise.all(requests)
            .then((results) => {
                if (STOREFRONT_PROMOTIONS_ENABLED) {
                    const [promoRes, tierRes, cartRes] = results;
                    const promotions = parsePromotionsListResponse(promoRes.data).map((promotion) => ({
                        ...promotion,
                        ruleKind: 'promotion',
                    }));
                    const tiers = parsePricingTiersListResponse(tierRes.data).map(mapPricingTierToListRow);
                    setRules(
                        [...promotions, ...tiers].sort((a, b) =>
                            (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
                        )
                    );
                    setCartRule(parseCartDiscountTierFromResponse(cartRes.data));
                    return;
                }

                const [tierRes, cartRes] = results;
                const tiers = parsePricingTiersListResponse(tierRes.data).map(mapPricingTierToListRow);
                setRules(
                    tiers.sort((a, b) =>
                        (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
                    )
                );
                setCartRule(parseCartDiscountTierFromResponse(cartRes.data));
            })
            .catch(() => {
                enqueueSnackbar('No se pudieron cargar las reglas de precios mayoristas', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            })
            .finally(() => setLoading(false));
    }, [storeId, enqueueSnackbar, promotionsFeatureEnabled]);

    useEffect(() => {
        loadRules();
    }, [loadRules]);

    const handleCreate = () => history.push(routes.promotionsCreate);

    const handleCreateTier = () => history.push(routes.promotionsCreateTier);

    const handleCreateCartTier = () => history.push(routes.promotionsCreateCartTier);

    const handlePrimaryCreate = () => {
        if (isCartSubtotalTab) {
            handleCreateCartTier();
            return;
        }
        handleCreateTier();
    };

    const handleEdit = (row) => {
        if (row.ruleKind === 'cart_subtotal') {
            history.push(routes.promotionsCartTierEdit);
            return;
        }
        if (row.ruleKind === 'quantity_discount' || !STOREFRONT_PROMOTIONS_ENABLED) {
            history.push(routes.promotionsTierEdit.replace(':id', row.id));
            return;
        }
        history.push(routes.promotionsEdit.replace(':id', row.id));
    };

    const handleDelete = async (row) => {
        const label =
            row.ruleKind === 'cart_subtotal'
                ? CART_SUBTOTAL_FEATURE_NAME
                : row.ruleKind === 'quantity_discount'
                  ? 'regla por cantidad'
                  : 'promoción';

        if (!window.confirm(`¿Eliminar ${label === 'promoción' ? 'la' : 'el'} ${label} "${row.name}"?`)) {
            return;
        }

        try {
            if (row.ruleKind === 'cart_subtotal') {
                await deleteCartDiscountTierService(storeId);
            } else if (row.ruleKind === 'quantity_discount') {
                await deletePricingTierService(storeId, row.id);
            } else {
                await deletePromotionService(storeId, row.id);
            }
            enqueueSnackbar('Regla eliminada', { variant: 'success', autoHideDuration: 2500 });
            loadRules();
        } catch {
            enqueueSnackbar(`No se pudo eliminar ${label === 'promoción' ? 'la' : 'el'} ${label}`, {
                variant: 'error',
                autoHideDuration: 3000,
            });
        }
    };

    const handleToggleActive = async (row) => {
        try {
            if (row.ruleKind === 'cart_subtotal') {
                await upsertCartDiscountTierService(
                    storeId,
                    buildCartDiscountTierTogglePayload(row)
                );
            } else if (row.ruleKind === 'quantity_discount') {
                await updatePricingTierService(
                    storeId,
                    row.id,
                    buildTierUpdatePayload({
                        name: row.name,
                        discount_type: row.discount_type,
                        thresholds: row.thresholds || [],
                        active: !row.active,
                        useSchedule: Boolean(row.starts_at || row.ends_at),
                        starts_at: row.starts_at ?? null,
                        ends_at: row.ends_at ?? null,
                    })
                );
            } else {
                await updatePromotionService(storeId, row.id, {
                    name: row.name,
                    discount_type: row.discount_type,
                    discount_value: row.discount_value,
                    price_strategy: row.price_strategy,
                    active: !row.active,
                    starts_at: row.starts_at ?? null,
                    ends_at: row.ends_at ?? null,
                });
            }
            enqueueSnackbar(
                !row.active ? 'Regla activada' : 'Regla desactivada',
                { variant: 'success', autoHideDuration: 3000 }
            );
            loadRules();
        } catch {
            enqueueSnackbar('No se pudo actualizar el estado', {
                variant: 'error',
                autoHideDuration: 3000,
            });
        }
    };

    const getRuleTypeLabel = (row) => {
        if (row.ruleKind === 'cart_subtotal') return CART_SUBTOTAL_TYPE_LABEL;
        if (row.ruleKind === 'quantity_discount') return 'Por cantidad';
        return 'Promoción vitrina';
    };

    const getDiscountLabel = (row) => {
        if (row.ruleKind === 'cart_subtotal') {
            return formatCartDiscountTierSummary(row, storeCurrency);
        }
        if (row.ruleKind === 'quantity_discount') {
            return formatTierRuleSummary(row, storeCurrency);
        }
        return formatPromotionDiscount(row, storeCurrency);
    };

    const getStrategyOrScopeLabel = (row) => {
        if (row.ruleKind === 'cart_subtotal') return 'Toda la tienda';
        if (row.ruleKind === 'quantity_discount') {
            return TIER_SCOPE_LABELS[row.scope] || row.scope || '—';
        }
        return getStrategyLabel(row.price_strategy);
    };

    const getStatusChip = (row) => {
        if (row.ruleKind === 'quantity_discount' || row.ruleKind === 'cart_subtotal') {
            return (
                <Chip
                    size="small"
                    label={row.active ? 'Activa' : 'Inactiva'}
                    color={row.active ? 'primary' : 'default'}
                />
            );
        }
        return (
            <Chip
                size="small"
                label={PROMOTION_STATUS_LABELS[row.status] || row.status}
            />
        );
    };

    const pageSubtitle = isCartSubtotalTab
        ? CART_SUBTOTAL_PAGE_SUBTITLE
        : STOREFRONT_PROMOTIONS_ENABLED
          ? 'Precios mayoristas en vitrina y descuentos por cantidad en el carrito.'
          : 'Descuentos por cantidad según tramos de volumen.';

    const emptyStateText = isCartSubtotalTab
        ? `No hay reglas todavía. Crea un ${CART_SUBTOTAL_FEATURE_NAME}.`
        : STOREFRONT_PROMOTIONS_ENABLED
          ? 'No hay reglas todavía. Crea una promoción o un descuento por cantidad.'
          : 'No hay reglas todavía. Crea un descuento por cantidad.';

    const primaryCreateLabel = isCartSubtotalTab
        ? 'Nueva regla'
        : STOREFRONT_PROMOTIONS_ENABLED
          ? 'Descuento por cantidad'
          : 'Nueva regla';

    const renderDiscountCell = (row) => {
        if (row.ruleKind === 'quantity_discount') {
            return <TierDiscountSummary tier={row} currency={storeCurrency} tableView />;
        }
        if (row.ruleKind === 'cart_subtotal') {
            return <CartDiscountTierSummary rule={row} currency={storeCurrency} tableView />;
        }
        return <Typography variant="body2">{getDiscountLabel(row)}</Typography>;
    };

    const renderTable = () => (
        <TableContainer
            component={Paper}
            elevation={0}
            className={tableClasses.tableContainer}
        >
            <Table size="small" className={tableClasses.table}>
                <TableHead>
                    <TableRow>
                        <TableCell className={`${tableClasses.headCell} ${tableClasses.nameCell}`}>
                            Nombre
                        </TableCell>
                        <TableCell className={`${tableClasses.headCell} ${tableClasses.typeCell}`}>
                            Tipo
                        </TableCell>
                        <TableCell className={`${tableClasses.headCell} ${tableClasses.discountCell}`}>
                            Descuento
                        </TableCell>
                        <TableCell className={`${tableClasses.headCell} ${tableClasses.scopeCell}`}>
                            Alcance / estrategia
                        </TableCell>
                        <TableCell className={`${tableClasses.headCell} ${tableClasses.statusCell}`}>
                            Estado
                        </TableCell>
                        <TableCell
                            align="center"
                            className={`${tableClasses.headCell} ${tableClasses.activeCell}`}
                        >
                            Activa
                        </TableCell>
                        <TableCell
                            align="right"
                            className={`${tableClasses.headCell} ${tableClasses.actionsCell}`}
                        >
                            Acciones
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={7} className={tableClasses.bodyCell}>
                                <Typography variant="body2" align="center">
                                    Cargando…
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {!loading && displayRules.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7} className={tableClasses.bodyCell}>
                                <Typography variant="body2" align="center" color="textSecondary">
                                    {emptyStateText}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {!loading &&
                        displayRules.map((row) => (
                            <TableRow key={`${row.ruleKind}-${row.id}`} hover>
                                <TableCell className={`${tableClasses.bodyCell} ${tableClasses.nameCell}`}>
                                    {row.name}
                                </TableCell>
                                <TableCell className={`${tableClasses.bodyCell} ${tableClasses.typeCell}`}>
                                    {getRuleTypeLabel(row)}
                                </TableCell>
                                <TableCell className={`${tableClasses.bodyCell} ${tableClasses.discountCell}`}>
                                    {renderDiscountCell(row)}
                                </TableCell>
                                <TableCell className={`${tableClasses.bodyCell} ${tableClasses.scopeCell}`}>
                                    {getStrategyOrScopeLabel(row)}
                                </TableCell>
                                <TableCell className={`${tableClasses.bodyCell} ${tableClasses.statusCell}`}>
                                    {getStatusChip(row)}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    className={`${tableClasses.bodyCell} ${tableClasses.activeCell}`}
                                >
                                    <Switch
                                        color="primary"
                                        checked={Boolean(row.active)}
                                        onChange={() => handleToggleActive(row)}
                                        disabled={
                                            row.ruleKind === 'promotion' && row.status === 'applying'
                                        }
                                    />
                                </TableCell>
                                <TableCell
                                    align="right"
                                    className={`${tableClasses.bodyCell} ${tableClasses.actionsCell}`}
                                >
                                    <Tooltip title="Editar">
                                        <IconButton size="small" onClick={() => handleEdit(row)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar">
                                        <IconButton size="small" onClick={() => handleDelete(row)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (!storeId) {
        return (
            <Box p={3}>
                <Typography variant="h6">{t('PromotionsTitle')}</Typography>
                <Typography variant="body2" color="textSecondary">
                    Selecciona una tienda para continuar.
                </Typography>
            </Box>
        );
    }

    if (!promotionsFeatureEnabled) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                width="100%"
                p={2}
                pb={isMobile ? 10 : 2}
            >
                <Box maxWidth={560} width="100%">
                    <LimitedFreemiumPlan type="payment" embedded />
                </Box>
            </Box>
        );
    }

    if (isMobile) {
        return (
            <PromotionsPageMobile
                rules={displayRules}
                loading={loading}
                onCreate={STOREFRONT_PROMOTIONS_ENABLED && !isCartSubtotalTab ? handleCreate : undefined}
                onCreateTier={handlePrimaryCreate}
                primaryCreateDisabled={isPrimaryCreateDisabled}
                storefrontPromotionsEnabled={STOREFRONT_PROMOTIONS_ENABLED}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
                storeCurrency={storeCurrency}
                getRuleTypeLabel={getRuleTypeLabel}
                getDiscountLabel={getDiscountLabel}
                getStatusChip={getStatusChip}
                pageSubtitle={pageSubtitle}
                emptyStateText={emptyStateText}
                primaryCreateLabel={primaryCreateLabel}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                renderDiscountCell={renderDiscountCell}
            />
        );
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                    <Typography variant="h5" style={{ fontWeight: 600 }}>
                        {t('PromotionsTitle')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {pageSubtitle}
                    </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginLeft: 16 }}>
                    {isPrimaryCreateDisabled ? (
                        <Tooltip title={CART_SUBTOTAL_CREATE_DISABLED_HINT}>
                            <span>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handlePrimaryCreate}
                                    disabled
                                    className={actionButtonClasses.actionButton}
                                >
                                    {primaryCreateLabel}
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Button
                            color="primary"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handlePrimaryCreate}
                            className={actionButtonClasses.actionButton}
                        >
                            {primaryCreateLabel}
                        </Button>
                    )}
                    {STOREFRONT_PROMOTIONS_ENABLED && !isCartSubtotalTab && (
                        <Button
                            color="primary"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreate}
                            className={actionButtonClasses.actionButton}
                        >
                            Crear promoción
                        </Button>
                    )}
                </Box>
            </Box>

            <PromotionsPageTabs activeTab={activeTab} onChange={handleTabChange} />
            <PromotionsBetaNotice />
            {renderTable()}
        </Box>
    );
};

const mapStateToProps = ({ auth, common }) => ({
    storeId: auth.storeId,
    storeData: common.storeData,
});

export default connect(mapStateToProps)(PromotionsPage);
